import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Avatar } from './components/Avatar.js';
import { ReportButton } from './components/Report.js';
import { useAccount } from './Account.js';
import { useResource } from './api.js';
import { ReviewContent } from './components/FragranceReviews.js';
import {
  CollectionRow,
  labels,
  type Collection,
  type CollectionFragrance,
} from './components/SavedFragrances.js';
import { reviewsVisible, type FragranceReview } from '../../../packages/shared/src/reviews.js';

type Profile = { username: string; name: string; avatarUrl?: string | null; total: number; page: number; adminOnly?: 'private' | 'unverified';
  collections?: Record<Collection, CollectionFragrance[]>;
  reviews: (Omit<FragranceReview, 'user'> & { fragrance: { slug: string; name: string; concentration: string; brand: { name: string } | null } })[] };
export function PublicProfile() {
  const { username = '' } = useParams();
  return <ProfilePage key={username} username={username}/>;
}
function ProfilePage({ username }: { username: string }) {
  const { user } = useAccount();
  const [page, setPage] = useState(1);
  const { data, loading, error, reload } = useResource<Profile>('/api/v1/profiles/' + encodeURIComponent(username) + '?page=' + page);
  return <section className="account-settings profile-page">
    <header><Link className="account-back" to="/">Back to the collection</Link><h1>{data ? data.name || data.username : 'Member profile'}</h1></header>
    {loading ? <p role="status">Loading profile…</p> : error ? <div className="profile-placeholder"><p role="alert">{error}</p><button className="button secondary" onClick={reload}>Try again</button></div> : data && <>
      {data.adminOnly && <p className="profile-admin-notice" role="note">{data.adminOnly === 'private' ? 'This profile is private.' : 'This profile is hidden until the email address is verified.'} You can see it because you’re signed in as an admin.</p>}
      <div className="profile-identity"><Avatar url={data.avatarUrl} size={88}/><div><h2>@{data.username}</h2><p>{data.adminOnly ? 'Not visible to the public' : 'Public profile'}</p>{!data.adminOnly && user?.username !== data.username && <ReportButton target="PROFILE" id={data.username} subject={'@' + data.username + '’s profile'}/>}</div></div>
      {data.collections && <PublicCollections collections={data.collections}/>}
      {reviewsVisible && <section className="public-profile-reviews" aria-labelledby="member-reviews"><h2 id="member-reviews">Reviews ({data.total})</h2>
        {data.total === 0 ? <p>No reviews yet.</p> : data.reviews.map(review => <article className="public-profile-review" key={review.id}>
          <h3><Link to={'/fragrances/' + review.fragrance.slug}>{review.fragrance.brand?.name} {review.fragrance.name}</Link></h3>
          <p>{review.fragrance.concentration} · <time dateTime={review.createdAt}>{new Date(review.createdAt).toLocaleDateString('en-GB')}</time>{user?.username !== data.username && <> · <ReportButton target="REVIEW" id={review.id} subject={'@' + data.username + '’s review of ' + review.fragrance.name}/></>}</p>
          <ReviewContent review={review}/>
        </article>)}
        {data.total > 10 && <nav className="review-pagination" aria-label="Profile review pages"><button className="button secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</button><span>Page {page} of {Math.ceil(data.total / 10)}</span><button className="button secondary" disabled={page * 10 >= data.total} onClick={() => setPage(p => p + 1)}>Next</button></nav>}
      </section>}
    </>}
  </section>;
}
function PublicCollections({ collections }: { collections: NonNullable<Profile['collections']> }) {
  const [collection, setCollection] = useState<Collection>(
    !collections.ALREADY_OWN.length && collections.WANT_TO_TRY.length ? 'WANT_TO_TRY' : 'ALREADY_OWN');
  const visible = collections[collection];
  return <section className="saved-collections public-profile-collections" aria-labelledby="member-collections">
    <h2 id="member-collections">Fragrance collections</h2>
    <div className="collection-switch" role="group" aria-label="Choose a collection">
      {(['ALREADY_OWN', 'WANT_TO_TRY'] as const).map(value => <button type="button" key={value} aria-pressed={collection === value} onClick={() => setCollection(value)}>
        {labels[value]} <span>{collections[value].length}</span>
      </button>)}
    </div>
    {visible.length
      ? <ul className="saved-fragrance-list">{visible.map(fragrance => <CollectionRow key={fragrance.id} fragrance={fragrance}/>)}</ul>
      : <p className="collection-empty-note">{collection === 'ALREADY_OWN' ? 'No fragrances in this shelf yet.' : 'No fragrances on this wishlist yet.'}</p>}
  </section>;
}
